import React from 'react';
import { Link } from 'react-router-dom';
import { Bell, Mail, Hash, Info, ExternalLink, Volume2 } from 'lucide-react';
import type { NotificationPrefs } from '../../types';
import {
  NOTIFICATION_EVENT_OPTIONS,
  CARD_EXPENSE_DIGEST_META,
  getCardExpenseDigestBadges,
  AUTOMATIC_IN_APP,
  TEAM_SLACK_NOTE,
  CHANNEL_COPY,
  getEventDeliveryBadges,
  type NotificationAudience,
} from '../../utils/notificationSettingsMeta';
import {
  BELL_SOUND_OPTIONS,
  playBellNotificationSound,
  resolveBellSoundId,
  unlockBellSound,
} from '../../utils/bellNotificationSound';
import './NotificationSettingsForm.css';

interface Props {
  prefs: NotificationPrefs;
  onChange: (prefs: NotificationPrefs) => void;
  variant: NotificationAudience;
  userEmail?: string;
}

const NotificationSettingsForm: React.FC<Props> = ({ prefs, onChange, variant, userEmail }) => {
  const isInternal = variant === 'internal';

  const togglePref = (key: keyof NotificationPrefs) => {
    onChange({ ...prefs, [key]: !prefs[key] });
  };

  return (
    <div className="notif-settings-form">
      <div className="notif-settings-scope">
        <Info size={16} aria-hidden />
        <p>{CHANNEL_COPY.personalScope}</p>
      </div>

      {isInternal && (
        <Link to="/notifications" className="notif-settings-history-link">
          View notification history
          <ExternalLink size={13} aria-hidden />
        </Link>
      )}

      <section className="notif-settings-section">
        <h3 className="notif-settings-section-title">Your channels</h3>

        <label className="notif-settings-row">
          <div className="notif-settings-row-icon" aria-hidden>
            <Mail size={16} />
          </div>
          <div className="notif-settings-row-body">
            <span className="notif-settings-row-label">{CHANNEL_COPY.email.title}</span>
            <span className="notif-settings-row-hint">
              {CHANNEL_COPY.email.description}
              {userEmail ? ` ${userEmail}` : ''}
            </span>
          </div>
          <button
            type="button"
            className={`notif-settings-switch ${prefs.email ? 'on' : ''}`}
            onClick={() => togglePref('email')}
            role="switch"
            aria-checked={prefs.email}
            aria-label="Email notifications"
          >
            <span className="notif-settings-switch-thumb" />
          </button>
        </label>

        <div className="notif-settings-row notif-settings-row--static">
          <div className="notif-settings-row-icon" aria-hidden>
            <Volume2 size={16} />
          </div>
          <div className="notif-settings-row-body">
            <span className="notif-settings-row-label">Play a sound</span>
            <span className="notif-settings-row-hint">
              Short chime when a new unread notification arrives in the bell.
            </span>
          </div>
          <div className="notif-settings-row-actions">
            <button
              type="button"
              className="notif-settings-test-btn"
              onClick={() => {
                unlockBellSound();
                playBellNotificationSound({ force: true, id: prefs.bellSoundId });
              }}
            >
              Test
            </button>
            <button
              type="button"
              className={`notif-settings-switch ${prefs.bellSound ? 'on' : ''}`}
              onClick={() => togglePref('bellSound')}
              role="switch"
              aria-checked={prefs.bellSound}
              aria-label="Play a sound for new bell notifications"
            >
              <span className="notif-settings-switch-thumb" />
            </button>
          </div>
        </div>

        <div className="notif-settings-sound-picker" role="listbox" aria-label="Notification sound">
          <div className="notif-settings-sound-chips">
            {BELL_SOUND_OPTIONS.map((opt) => {
              const selected = resolveBellSoundId(prefs.bellSoundId) === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  title={opt.hint}
                  className={`notif-settings-sound-chip${selected ? ' is-selected' : ''}`}
                  onClick={() => {
                    onChange({ ...prefs, bellSoundId: opt.id });
                    unlockBellSound();
                    playBellNotificationSound({ force: true, id: opt.id });
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="notif-settings-section">
        <h3 className="notif-settings-section-title">Task &amp; Operations alerts</h3>
        <p className="notif-settings-section-desc">
          Turn off anything you don’t want. Comments and @mentions only go to people on the task — not the whole team.
        </p>

        <ul className="notif-settings-event-list">
          {NOTIFICATION_EVENT_OPTIONS.map((event) => {
            const enabled = !!prefs[event.key];
            const badges = getEventDeliveryBadges(prefs, variant, event.key, enabled);
            return (
              <li key={event.key}>
                <label className="notif-settings-event-row">
                  <div className="notif-settings-event-copy">
                    <span className="notif-settings-event-label">{event.label}</span>
                    <span className="notif-settings-event-hint">{event.hint}</span>
                    <span className="notif-settings-event-badges" aria-label="Delivery channels">
                      {badges.map((badge) => (
                        <span
                          key={`${event.key}-${badge.kind}`}
                          className={`notif-settings-event-badge notif-settings-event-badge--${badge.kind}`}
                        >
                          {badge.label}
                        </span>
                      ))}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`notif-settings-switch ${enabled ? 'on' : ''}`}
                    onClick={() => togglePref(event.key)}
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`${event.label} notifications`}
                  >
                    <span className="notif-settings-switch-thumb" />
                  </button>
                </label>
              </li>
            );
          })}
        </ul>
      </section>

      {isInternal && (
        <section className="notif-settings-section">
          <h3 className="notif-settings-section-title">Finance alerts</h3>
          <ul className="notif-settings-event-list">
            <li>
              <label className="notif-settings-event-row">
                <div className="notif-settings-event-copy">
                  <span className="notif-settings-event-label">{CARD_EXPENSE_DIGEST_META.label}</span>
                  <span className="notif-settings-event-hint">{CARD_EXPENSE_DIGEST_META.hint}</span>
                  <span className="notif-settings-event-badges" aria-label="Delivery channels">
                    {getCardExpenseDigestBadges(prefs, !!prefs.onCardExpenseDigest).map(badge => (
                      <span
                        key={`card-digest-${badge.kind}`}
                        className={`notif-settings-event-badge notif-settings-event-badge--${badge.kind}`}
                      >
                        {badge.label}
                      </span>
                    ))}
                  </span>
                </div>
                <button
                  type="button"
                  className={`notif-settings-switch ${prefs.onCardExpenseDigest ? 'on' : ''}`}
                  onClick={() => togglePref('onCardExpenseDigest')}
                  role="switch"
                  aria-checked={!!prefs.onCardExpenseDigest}
                  aria-label={`${CARD_EXPENSE_DIGEST_META.label} notifications`}
                >
                  <span className="notif-settings-switch-thumb" />
                </button>
              </label>
            </li>
          </ul>
        </section>
      )}

      {isInternal && (
        <section className="notif-settings-section notif-settings-section--muted">
          <h3 className="notif-settings-section-title">{CHANNEL_COPY.automatic.title}</h3>
          <div className="notif-settings-auto-grid">
            <div className="notif-settings-auto-card">
              <Bell size={15} aria-hidden />
              <div>
                <strong>{CHANNEL_COPY.automatic.inApp}</strong>
                <ul>
                  {AUTOMATIC_IN_APP.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="notif-settings-auto-card">
              <Hash size={15} aria-hidden />
              <div>
                <strong>{CHANNEL_COPY.automatic.teamSlack}</strong>
                <p>{TEAM_SLACK_NOTE}</p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NotificationSettingsForm;
